import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Form, FormButtons, FormField, FormProps, FormSchemaEntry, FormType, HTMLParser } from '@gik/ui';
import { featureCareCalendarSchema, featureDonationsSchema, featureWishlistSchema } from '@gik/create/formSchemas';
import { FormRenderProps } from 'react-final-form';
import { CreateAnalyticsEvents } from '@gik/create/hooks/useCreateAnalytics';
import { useCreateFlowStore } from '@gik/create';
import { FeatureCareCalendarInputNames } from '@gik/create/enums/FeatureCareCalendarInputNames';
import { FeatureWishlistInputNames } from '@gik/create/enums/FeatureWishlistInputNames';
import { FeatureDonationsInputNames } from '@gik/create/enums/FeatureDonationsInputNames';
import { FeaturesStepInputNames } from '@gik/create/enums/FeaturesStepInputNames';

export interface IBaseFeaturesFormValues extends FormProps {}

export interface ICreateFeaturesCareCalendarFormValues extends IBaseFeaturesFormValues {
  [FeatureCareCalendarInputNames.CareCalendarEnabled]: boolean;
}

export interface ICreateFeaturesWishlistFormValues extends IBaseFeaturesFormValues {
  [FeatureWishlistInputNames.WishlistEnabled]: boolean;
  [FeatureWishlistInputNames.WishlistItems]: number[];
}

export interface ICreateFeaturesDonationsFormValues extends IBaseFeaturesFormValues {
  [FeatureDonationsInputNames.DonationsEnabled]: boolean;
}

export interface IFeatureStepProps<T extends IBaseFeaturesFormValues> {
  title: string;
  description: string;
  finePrint?: string;
  icon: React.ReactNode;
  className?: string;
  formName: FeaturesStepInputNames;
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  formId?: string;
  onSubmit?: (values: T) => void;
  onChange?: (values: T) => void;
}

const blockName = 'feature-step';

function FeatureStep<T extends IBaseFeaturesFormValues>(
  {
    title,
    description,
    finePrint,
    icon,
    className,
    formName,
    formId,
    buttons,
    buttonsPortal,
    ...otherProps
  }: IFeatureStepProps<T>,
  ref
) {
  const bem = bemBlock(blockName);
  const formRef = React.useRef<FormType>(null);
  const formValues = useCreateFlowStore(state => state.formValues);

  React.useImperativeHandle(ref, () => formRef.current);

  let schema: FormSchemaEntry[];
  switch (formName) {
    case FeaturesStepInputNames.CareCalendar:
      schema = featureCareCalendarSchema(formValues);
      break;
    case FeaturesStepInputNames.Wishlist:
      schema = featureWishlistSchema(formValues);
      break;
    case FeaturesStepInputNames.Donations:
      schema = featureDonationsSchema(formValues);
      break;
  }

  return (
    <Form
      schema={schema}
      ref={formRef}
      className={bem(null, null, className)}
      trackingId={CreateAnalyticsEvents.CREATE_STARTED}
      render={form => {
        return (
          <div>
            <section className={bem('title-wrapper')}>
              <div className={bem('icon')}>{icon}</div>
              <div className={bem('title')}>{title}</div>
            </section>

            <p className={bem('description')}>
              <HTMLParser rawHtml={description} />
            </p>
            {schema.map((field, key) => (
              <FormField key={key} className={bem('form-field')} name={field.name} center />
            ))}

            <FormButtons buttons={buttons} buttonsPortal={buttonsPortal} form={form} formId={formId} />
            {finePrint && <p className={bem('fine-print')}>{finePrint}</p>}
          </div>
        );
      }}
      {...otherProps}
    />
  );
}

export const CareCalendarFeatureStep = React.forwardRef<
  FormType,
  IFeatureStepProps<ICreateFeaturesCareCalendarFormValues>
>(FeatureStep);

export const WishlistFeatureStep = React.forwardRef<FormType, IFeatureStepProps<ICreateFeaturesWishlistFormValues>>(
  FeatureStep
);

export const DonationsFeatureStep = React.forwardRef<FormType, IFeatureStepProps<ICreateFeaturesDonationsFormValues>>(
  FeatureStep
);
